import { motion } from 'framer-motion';
import { Brain, Sparkles, FileText, Map, Calendar, Shield } from 'lucide-react';

interface GenerationProgressProps {
  currentStep: string;
  progress: number;
  documentLabels?: {
    doc1: string;
    doc2: string;
    doc3?: string;
  };
  isMultiPart?: boolean;
  currentPart?: number;
  totalParts?: number;
}

export default function GenerationProgress({ currentStep, progress, documentLabels, isMultiPart, currentPart, totalParts }: GenerationProgressProps) {
  // Labels par défaut (BesoinsWizard)
  const labels = documentLabels || {
    doc1: "Spécifications d'exigences d'affaires et TI",
    doc2: 'Roadmap annuel',
    doc3: 'Plan directeur'
  };
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-bz-dark via-bz-blue to-purple-900 z-50 flex items-center justify-center">
      <div className="max-w-2xl w-full px-8">
        {/* Einstein Bot qui réfléchit */}
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            {/* Cercle de lumière pulsante */}
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-yellow-400 rounded-full blur-3xl"
            />
            
            {/* Einstein Bot */}
            <div className="relative bg-white rounded-full p-8 shadow-2xl">
              <Brain className="w-24 h-24 text-bz-blue" />
              
              {/* Particules de pensée */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [0, -30, -60],
                    x: [0, Math.sin(i) * 20, Math.sin(i) * 40],
                    opacity: [1, 0.5, 0],
                    scale: [0.5, 1, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeOut"
                  }}
                  className="absolute top-0 left-1/2"
                  style={{ marginLeft: '-4px' }}
                >
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Status texte */}
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-center mb-6"
        >
          <h3 className="text-3xl font-bold text-white mb-2">
            Intelligence Artificielle en action...
          </h3>
          <p className="text-white/80 text-lg">{currentStep}</p>
        </motion.div>

        {/* Progress Bar */}
        <div className="relative mb-8">
          <div className="bg-white/20 backdrop-blur-sm rounded-full h-4 overflow-hidden border border-white/30">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 h-4 rounded-full relative"
            >
              {/* Effet de brillance */}
              <motion.div
                animate={{
                  x: ['-100%', '200%'],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
              />
            </motion.div>
          </div>
          
          {/* Pourcentage */}
          <div className="flex justify-between mt-2 text-white/70 text-sm">
            <span>
              {isMultiPart && currentPart && totalParts 
                ? `Partie ${currentPart}/${totalParts} - ${currentStep}`
                : 'Analyse en cours'}
            </span>
            <span className="font-bold">{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Documents en cours de génération */}
        <div className={`grid gap-4 ${labels.doc3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <DocumentIndicator 
            icon={FileText} 
            label={labels.doc1}
            isActive={progress > 0}
            isComplete={labels.doc3 ? progress > 33 : progress > 50}
          />
          <DocumentIndicator 
            icon={labels.doc3 ? Calendar : Shield} 
            label={labels.doc2}
            isActive={labels.doc3 ? progress > 33 : progress > 50}
            isComplete={labels.doc3 ? progress > 66 : progress >= 100}
          />
          {labels.doc3 && (
            <DocumentIndicator 
              icon={Map} 
              label={labels.doc3}
              isActive={progress > 66}
              isComplete={progress >= 100}
            />
          )}
        </div>

        {/* Citation inspirante */}
        <motion.div
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="text-center mt-8 text-white/60 italic text-sm"
        >
          "L'intelligence artificielle au service de l'excellence TI"
        </motion.div>
      </div>
    </div>
  );
}

function DocumentIndicator({ 
  icon: Icon, 
  label, 
  isActive, 
  isComplete 
}: { 
  icon: any; 
  label: string; 
  isActive: boolean; 
  isComplete: boolean;
}) {
  return (
    <div className={`
      relative p-4 rounded-lg border-2 transition-all duration-500
      ${isComplete 
        ? 'bg-green-500/20 border-green-400' 
        : isActive 
          ? 'bg-white/10 border-white/50' 
          : 'bg-white/5 border-white/20'
      }
    `}>
      <div className="flex flex-col items-center gap-2">
        {isActive && !isComplete && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <Icon className="w-6 h-6 text-white" />
          </motion.div>
        )}
        {!isActive && <Icon className="w-6 h-6 text-white/40" />}
        {isComplete && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <Icon className="w-6 h-6 text-green-400" />
          </motion.div>
        )}
        <span className={`text-xs text-center ${
          isComplete ? 'text-green-400 font-bold' : 'text-white/70'
        }`}>
          {label}
        </span>
      </div>
    </div>
  );
}

